/**
 * TA Cockpit → Planning.
 *
 * "What is being asked of us, who do we have, and what do we commit to?" — in that order, which is
 * the order the decision is actually made in.
 *
 *   01  DEMAND      official (HDB) and proposed (not approved), never added together
 *   02  COVERAGE    what the roster below can deliver, per cluster
 *   03  THE TEAM    the one roster every tab plans against
 *   04  ASSUMPTIONS hires per month first, because it is the number everything multiplies out from
 *   05  COMMIT      the trimester's target, and the baseline it will be measured against
 *   06  SERVICE LEVELS  what each priority rating costs TA per role per week — read on the Company
 *                       View, decided here
 *
 * ⚠️ THE THREE NUMBERS, kept apart on purpose: demand is what was asked for, capacity is what the
 * model says we can do, and the TARGET is what TA commits to. The last one is a decision, and it
 * lives on the server instead of in whichever browser happened to take it.
 *
 * ⚠️ NO PROSE. Every explanation on this tab is behind the ⓘ beside the thing it explains. A
 * planning screen is read in a meeting; paragraphs between the figures are paragraphs nobody reads
 * and figures nobody finds.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../../../shared';
import { fetchTracking } from '../../../services/taTrackingApi';
import {
  commitCommittedTarget,
  fetchCommittedTarget,
} from '../../../services/taCockpitTargetApi';
import { TeamRejectedError, fetchTeam, saveTeam } from '../../../services/taTeamApi';
import type { StoredTarget, TrackingData } from '../../../types/taTracking';
import { SEED_TEAM, type ClusterKey, type TeamMember } from '../../../types/taTeam';
import {
  CLUSTER_KEYS,
  DEFAULT_ASSUMPTIONS,
  actualHpm,
  clusterTeamsFrom,
  computeCapacity,
  monthsElapsed,
  type CapacityAssumptions,
  type Horizon,
} from '../../../utils/taCapacityModel';
import { Assumptions } from './Assumptions';
import { Coverage } from './Coverage';
import { CommitTarget } from './CommitTarget';
import { DemandStrip } from './DemandStrip';
import { TeamAndModel } from './TeamAndModel';
import { TrimesterBaseline } from './TrimesterBaseline';
import { ServiceLevelModel } from '../rolePrioritisation/ServiceLevelModel';
import { useRolePrioritisationState } from '../rolePrioritisation/RolePrioritisationProvider';
import { logAnalyticsEvent } from '../../../../../analytics/nativeAnalytics';

const HORIZONS: { value: Horizon; label: string }[] = [
  { value: 'trimester', label: 'Whole trimester' },
  { value: 'remaining', label: 'Rest of the trimester' },
];

const sameTeam = (a: TeamMember[], b: TeamMember[]) => JSON.stringify(a) === JSON.stringify(b);

export function CapacityTab({ period }: { period: string }) {
  const { reloadTeam } = useRolePrioritisationState();
  const [tracking, setTracking] = useState<TrackingData | null>(null);
  const [committed, setCommitted] = useState<StoredTarget | null>(null);
  /** ⚠️ Distinct from `committed === null`, which means "nobody has set one". */
  const [targetError, setTargetError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState<Horizon>('trimester');
  const [assumptions, setAssumptions] = useState<CapacityAssumptions>(DEFAULT_ASSUMPTIONS);

  /** The roster as the server has it, and the roster on screen. Equal until someone edits. */
  const [savedTeam, setSavedTeam] = useState<TeamMember[] | null>(null);
  const [savedBy, setSavedBy] = useState<string | null>(null);
  const [members, setMembers] = useState<TeamMember[]>(SEED_TEAM);
  const [teamBusy, setTeamBusy] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  /**
   * ⚠️ WHY THE READ FAILED IS ITS OWN STATE, and Save is blocked while it is set.
   *
   * Swallowing the error to `null` made a transient outage indistinguishable from "nobody has saved
   * a roster yet" — so the tab would show the seeded names, offer Save, and one click would replace
   * the real shared roster with a team nobody assembled. Same distinction the demand figures
   * already make: "could not be read" is not "none exists".
   */
  const [teamReadError, setTeamReadError] = useState<string | null>(null);

  /**
   * ⚠️ Bumped on every load, and checked before every state write.
   *
   * `TACockpitView` recomputes the period each render, so a render crossing a trimester boundary
   * starts a second load in the SAME component instance — and the first one, resolving later, would
   * write the old period's demand into state that the commit button then stores under the new one.
   */
  const run = useRef(0);

  const load = useCallback(() => {
    const mine = (run.current += 1);
    const fresh = run.current === 1;
    if (!fresh) {
      setTracking(null);
      setCommitted(null);
    }
    return Promise.all([
      fetchTracking(period),
      fetchCommittedTarget(period).then(
        (c) => ({ target: c.target, error: null as string | null }),
        (e: unknown) => ({ target: null, error: e instanceof Error ? e.message : String(e) }),
      ),
      fetchTeam().then(
        (team) => ({ team, error: null as string | null }),
        (e: unknown) => ({ team: null, error: e instanceof Error ? e.message : String(e) }),
      ),
    ])
      .then(([t, c, roster]) => {
        if (run.current !== mine) return;
        setTracking(t);
        setCommitted(c.target);
        setTargetError(c.error);
        setError(null);
        setTeamReadError(roster.error);
        /*
         * `members` is checked for an ARRAY, not for length: an empty roster is a real answer —
         * somebody can legitimately clear the team — and treating it as "nothing saved" would
         * restore the seed on the next load and silently add back capacity they removed.
         */
        if (roster.team && Array.isArray(roster.team.members)) {
          const next = roster.team.members;
          setSavedTeam(next);
          setSavedBy(roster.team.savedBy || null);
          /*
           * Only adopt the server's roster when the screen has no unsaved edits. A refresh landing
           * mid-edit would otherwise discard work somebody is in the middle of.
           */
          setMembers((cur) => (savedTeam && !sameTeam(cur, savedTeam) ? cur : next));
        }
      })
      .catch((e) => {
        if (run.current !== mine) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (run.current === mine) setLoading(false);
      });
    // `savedTeam` is read inside to protect an in-flight edit; including it would re-fetch on every
    // save, and the read is deliberately of whatever it was when the response landed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * ⚠️ WHICH demand figure the capacity is measured against depends on the horizon, and getting
   * this wrong is the easiest way to make the tab lie. Over the WHOLE trimester the question is the
   * whole plan. Over what is LEFT of it, the hires already made are done — measuring four months of
   * plan against six weeks of capacity would report a team that is delivering as one that is
   * failing.
   */
  const demandByCluster = useMemo(() => {
    const out = { Growth: 0, Operations: 0, 'Core Functions': 0 } as Record<ClusterKey, number>;
    for (const c of tracking?.clusters ?? []) {
      if ((CLUSTER_KEYS as readonly string[]).includes(c.key)) {
        out[c.key as ClusterKey] = horizon === 'remaining' ? c.toRecruit : c.target;
      }
    }
    return out;
  }, [tracking, horizon]);

  /**
   * Demand no cluster could be attributed to. Added to the company total and to no cluster — the
   * same rule the Tracking tab follows, because a role filed under a guessed cluster moves the
   * figure that cluster's lead is judged on.
   */
  const unattributed = useMemo(() => {
    if (!tracking) return 0;
    const whole = horizon === 'remaining' ? tracking.toRecruit : tracking.target.demand;
    const perCluster = Object.values(demandByCluster).reduce((s, n) => s + n, 0);
    return Math.max(0, whole - perCluster);
  }, [tracking, demandByCluster, horizon]);

  const result = useMemo(
    () =>
      computeCapacity({
        members,
        assumptions,
        demandByCluster,
        unattributedDemand: unattributed,
        horizon,
        elapsedDays: tracking?.pace.elapsedDays ?? 0,
        totalDays: tracking?.pace.totalDays ?? 0,
      }),
    [members, assumptions, demandByCluster, unattributed, horizon, tracking],
  );

  /** The measured rate, so the assumption can be checked rather than believed. */
  const actualByCluster = useMemo(() => {
    const teams = clusterTeamsFrom(members, assumptions.fullWeekHours);
    const elapsed = monthsElapsed(tracking?.pace.elapsedDays ?? 0, tracking?.pace.totalDays ?? 0);
    const out = {} as Record<ClusterKey, number | null>;
    for (const t of teams) {
      const hired = tracking?.clusters.find((c) => c.key === t.key)?.hired ?? 0;
      out[t.key] = actualHpm(hired, t.fte, elapsed);
    }
    return out;
  }, [members, assumptions.fullWeekHours, tracking]);

  const fteByCluster = useMemo(() => {
    const out = {} as Record<ClusterKey, number>;
    for (const t of clusterTeamsFrom(members, assumptions.fullWeekHours)) out[t.key] = t.fte;
    return out;
  }, [members, assumptions.fullWeekHours]);

  const dirty = savedTeam === null ? true : !sameTeam(members, savedTeam);

  const onSaveTeam = async () => {
    setTeamBusy(true);
    setTeamError(null);
    try {
      const stored = await saveTeam(members);
      // Counts only — how many people the roster holds, never who is on it.
      void logAnalyticsEvent('ta_cockpit_team_saved', { members: stored.members.length });
      setSavedTeam(stored.members);
      setSavedBy(stored.savedBy || null);
      setMembers(stored.members);
      /*
       * ⚠️ AND TELL THE REST OF THE COCKPIT. The prioritisation state reads this roster once, when
       * its provider mounts — and that provider now lives for the whole session, so without this
       * the Company View would price capacity against the team as it was when the page loaded and
       * its source bar would still say "seeded baseline" after a real roster had just been saved.
       */
      reloadTeam();
    } catch (e) {
      setTeamError(
        e instanceof TeamRejectedError
          ? e.message
          : `Could not save the team: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setTeamBusy(false);
    }
  };

  if (loading && !tracking) {
    return (
      <div className="flex h-48 items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Reading the hiring demand…
      </div>
    );
  }

  if (error && !tracking) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-sm text-rose-700">
        <AlertCircle className="h-5 w-5" />
        <div>Could not read the hiring demand: {error}</div>
        <p className="max-w-md text-center text-gray-500">
          Capacity is only meaningful against a demand figure, so there is nothing honest to plan
          with until the Hiring Database can be read.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setLoading(true);
            void load();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!tracking) return null;

  /*
   * ⚠️ PREFILLED FROM `covered`, not from capacity. `min(demand, capacity)` is the pooled answer
   * again — it would offer a target one hire above what any cluster can actually deliver, which is
   * precisely the arithmetic the coverage figure was corrected for.
   */
  const suggestion = Math.round(result.covered);
  const basis = {
    demand: result.demand,
    capacity: result.capacity,
    covered: result.covered,
    demandSource: tracking.target.demandSource,
    horizonMonths: result.months,
    recruiterFte: Object.values(fteByCluster).reduce((s, n) => s + n, 0),
    proposed: tracking.proposed?.proposed ?? 0,
    unraisedBackfills: assumptions.unraisedBackfills,
  };

  return (
    <div className="space-y-5">
      <DemandStrip tracking={tracking} committed={committed} targetError={targetError} />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500">Plan over</span>
        {HORIZONS.map((h) => (
          <Button
            key={h.value}
            variant={horizon === h.value ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setHorizon(h.value)}
            aria-pressed={horizon === h.value}
            className={cn('!rounded-full', horizon === h.value && 'font-semibold')}
          >
            {h.label}
          </Button>
        ))}
      </div>

      <Coverage
        result={result}
        horizonLabel={horizon === 'remaining' ? 'What is left to do' : 'The whole trimester'}
        committedTarget={committed?.target ?? null}
      />

      <TeamAndModel
        members={members}
        saved={savedTeam !== null}
        savedBy={savedBy}
        dirty={dirty}
        busy={teamBusy}
        readError={teamReadError}
        error={teamError}
        fullWeekHours={assumptions.fullWeekHours}
        onChange={setMembers}
        onSave={() => void onSaveTeam()}
        onDiscard={() => setMembers(savedTeam ?? SEED_TEAM)}
      />

      <Assumptions
        assumptions={assumptions}
        actualHpmByCluster={actualByCluster}
        fteByCluster={fteByCluster}
        onChange={setAssumptions}
        onReset={() => setAssumptions(DEFAULT_ASSUMPTIONS)}
        edited={JSON.stringify(assumptions) !== JSON.stringify(DEFAULT_ASSUMPTIONS)}
      />

      <CommitTarget
        /*
         * Keyed by period so a trimester rollover cannot carry a half-typed draft into the new one
         * — the component keeps `value`, `note` and `touched` in state, and `period` alone changing
         * would submit the old trimester's number against the new one.
         */
        key={period}
        period={period}
        committed={committed}
        suggestion={suggestion}
        demand={result.demand}
        covered={result.covered}
        basis={basis}
        onCommit={async (target, note) => {
          const { target: saved } = await commitCommittedTarget(period, { target, note, basis });
          // The number IS the decision here and it is a team commitment, not personal data —
          // whether a note was written travels as a boolean, never its text.
          void logAnalyticsEvent('ta_cockpit_target_committed', {
            target,
            withNote: Boolean(note),
          });
          setCommitted(saved);
        }}
      />

      <TrimesterBaseline period={period} initialSnapshots={tracking.snapshots ?? []} />

      {/*
        ⚠️ LAST ON THE TAB, and that is its whole placement argument. What a priority rating costs
        is a TA decision that belongs with the roster and the target — but it is re-cut a few times
        a year, while everything above it is read every week. It arrived from Role Prioritisation
        on Sep 25 2026, when that tab became the Company View.
      */}
      <ServiceLevelModel />
    </div>
  );
}
